import { Box, Typography, Container, Stack } from '@mui/material'
import { ScanSearch } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { IconBadge } from 'components/UI/IconBadge'

/**
 * Where a scan that could not be resolved to a certificate lands.
 *
 * The fifth sibling of `pages/TagReplaced`, `pages/TagNotActive`,
 * `pages/CertificateNotActive` and `pages/SomethingWentWrong` — read those files' headers
 * first, this one borrows their layout and their doctrine.
 *
 * ## WHY THE COPY WAS REWRITTEN (the page named the wrong input entirely)
 *
 * It used to say "The QR code or link you scanned is not valid or has expired. Please try
 * scanning a valid QR code". Nothing about that was true of the people who reach it.
 *
 * This route is reached ONLY by redirect from `ScanCertificateUseCase` — a TAG scan. A QR
 * code is a different product surface altogether (the profile code at `/u/<code>`, which
 * has its own handling and never redirects here). So the page named an input the reader
 * was not using, and then instructed them to "try scanning a valid QR code", which is
 * advice a person holding a physical object cannot act on and would not understand.
 *
 * ## WHAT IT MAY CALL THE THING (product rule, and it is a hard one)
 *
 * NEVER the tag technology. Not the chip, not the chip family, not the radio standard.
 * That is internal fulfilment detail and must never reach a customer — it is also
 * meaningless to them. The permitted vocabulary is exactly two options, chosen by whether
 * the redirect could confirm a tag was involved:
 *
 *   `?id=<tag_uid>` present → "Vera tag". `handleInvalidScan` sets it, so a tag was
 *                             physically read and we know its uid.
 *   `?id=` absent           → "this link". The generic `catch` in `ScanCertificateUseCase`
 *                             redirects with only `?type=`, so the request failed before
 *                             anything about a tag was established. Naming a tag there
 *                             would be a guess, and guessing wrong tells someone holding a
 *                             genuine item that their tag failed when it may never have
 *                             been read at all.
 *
 * Never "QR" in either branch.
 *
 * ## WHY THE REASSURANCE LINE IS NOT PADDING
 *
 * Same reasoning as `pages/SomethingWentWrong`, and it is the most load-bearing sentence
 * here. The reader is holding a physical luxury good and has just been told the
 * authenticity check did not work. The natural inference from that, with nothing else
 * said, is "the item failed the authenticity check". The page has to state plainly and
 * early that the item is not what is in question.
 *
 * ## WHY IT POINTS AT THE BRAND, NOT AT VERA
 *
 * Deliberate, and NOT an inconsistency with `SomethingWentWrong` to be tidied up. That
 * page is about VERA BEING BROKEN (a 500 in Vera's own endpoint), which the brand's desk
 * cannot reproduce or fix, so it routes to Vera. This page is about a scan not matching
 * anything, which is a question about the ITEM — genuinely the brand's business, and the
 * brand is who can look it up. The routing rule is "whoever can act on it". The visitor
 * also believes they are dealing with the brand: this app is served from the company's
 * installation on their subdomain and inherits their theme, so it carries no Vera identity
 * of its own.
 *
 * ⚠️ `/invalid-scan` must stay in STANDALONE_ENTRY_PATHS in `index.tsx`, or MemoryRouter
 * ignores the path, boots at '/', and bounces the visitor to SIGN-IN. See the comment on
 * that list — this page is one of the instances that trap has already caught.
 */
export default function InvalidScans() {
  const [searchParams] = useSearchParams()

  /*
   * Trimmed, because an empty-string `?id=` must be treated as absent — the branch below
   * turns on "do we actually know a tag was read", and `''` is not knowing.
   *
   * The uid itself is deliberately NOT rendered. It is an opaque identifier that tells the
   * reader nothing, and echoing hardware ids onto a page a stranger may be looking at buys
   * exposure for no benefit. It is used only to decide what to call the thing.
   */
  const scannedTag = Boolean(searchParams.get('id')?.trim())

  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
        }}
      >
        <Stack spacing={3} alignItems='center'>
          {/*
            The shared `IconBadge` — the grey circle every state screen in this app opens
            with. A magnifier over a scan frame, in the ALERT tone.

            A `ShieldAlert` was the obvious candidate, since `AuthenticChip`'s header calls
            the red shield the glyph "the invalid-scan surfaces already own" and
            `pages/NotFound`'s scan variant uses it. It is rejected HERE for the reason that
            file records from its own rejected experiment: a shield grades the ITEM. On this
            page the whole point of the copy below is that the item is not in question, so a
            shield would contradict the sentence beneath it. `ScanSearch` names the LOOKUP
            that failed instead, which is what actually happened.
          */}
          <IconBadge icon={ScanSearch} tone='alert' />

          <Stack spacing={2} alignItems='center'>
            {/*
              The title branches too. "This scan" is only honest when we know something was
              scanned; on the fallback branch the request failed before that was
              established, so it names the URL and nothing else.
            */}
            <Typography variant='h4' component='h1'>
              {scannedTag ? "We couldn't verify this scan" : "We couldn't verify this link"}
            </Typography>

            {/*
              Separate sentences rather than clauses joined by em-dashes (§15). lineHeight
              1.5 so the copy does not read dense on a phone, which is the only device this
              page is ever seen on.

              "Vera tag" or "this link" — never the tag technology, and never "QR". Note the
              retry instruction branches with it: the confirmed branch can say "tap it
              again" because we know a tag was read, and the fallback CANNOT, because
              telling someone to tap a tag we never confirmed they have is the same guess
              the header forbids in the noun.
            */}
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              {scannedTag
                ? "We couldn't match this Vera tag to a certificate. There is nothing wrong with your item. Please try tapping it again in a moment."
                : "We couldn't match this link to a certificate. There is nothing wrong with your item. Please try again in a moment."}
            </Typography>

            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              If it keeps happening, contact the brand you bought it from. They can look it
              up for you.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Container>
  )
}
